import { elapsedSince, localClockStamp, type LocalClockStamp } from './relayClock';

/** G1-only evidence, never a field on a Hydra market/book or a trading payload.
 * Age means producer observation/change age, NOT venue event time or liveness.
 * The one-way upstream transit delay is unknown; this is not a certified upper
 * bound on end-to-end age. */
export interface SourceAge {
  ageMs: number | null;
  sourceMs: number | null;
  sourceWatermarkMs: number | null;
  publicationMs: number | null;
  kind: string;
  generation: string;
  discontinuous: boolean;
  received: LocalClockStamp;
}

interface Frame {
  type?: string;
  serverTs?: unknown;
  market?: { lastUpdateMs?: unknown; updatedAtMs?: unknown };
  staleness?: { lastUpdateMs?: unknown; ageMs?: unknown };
  consensus?: { lastUpdateMs?: unknown };
}
const epoch = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const age = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

export function captureSourceAge(frame: Frame, generation: string): SourceAge {
  let source: unknown;
  let explicitAge: unknown;
  let kind = 'unknown';
  if (frame.type === 'market') {
    // A malformed advertised field is not permission to fall through to an
    // unrelated timestamp. Core and Edge contracts are independently known.
    const edge = frame.market?.lastUpdateMs;
    source = edge === undefined ? frame.market?.updatedAtMs : edge;
    kind = edge === undefined ? 'core-change' : 'market-observation';
  } else if (frame.type === 'orderbook') {
    source = frame.staleness?.lastUpdateMs;
    explicitAge = frame.staleness?.ageMs;
    kind = 'book-observation';
  } else if (frame.type === 'consensus') {
    source = frame.consensus?.lastUpdateMs;
    kind = 'consensus-computation';
  }
  const publication = frame.serverTs;
  const delta = epoch(source) && epoch(publication) ? publication - source : null;
  let sourceAge = delta != null && age(delta) ? delta : null;
  if (explicitAge !== undefined) {
    sourceAge = age(explicitAge) && sourceAge != null ? Math.max(explicitAge, sourceAge) : null;
  }
  return {
    ageMs: sourceAge, sourceMs: epoch(source) ? source : null,
    sourceWatermarkMs: epoch(source) ? source : null,
    publicationMs: epoch(publication) ? publication : null, kind, generation,
    discontinuous: false, received: localClockStamp(),
  };
}

export function sourceAgeNow(evidence: SourceAge | undefined): number | null {
  if (!evidence || evidence.ageMs == null || evidence.discontinuous) return null;
  const elapsed = elapsedSince(evidence.received);
  const current = evidence.ageMs + elapsed;
  return age(elapsed) && age(current) ? current : null;
}

/** Identical/older evidence within one source incarnation cannot become young
 * on replay. A backwards producer publication invalidates that incarnation's
 * clock evidence until a new connection; it does not discard the market value. */
export function retainSourceAge(previous: SourceAge | undefined, next: SourceAge): SourceAge {
  if (!previous || previous.generation !== next.generation || previous.kind !== next.kind) return next;
  const watermark = Math.max(previous.sourceWatermarkMs ?? 0, next.sourceMs ?? 0) || null;
  if (previous.discontinuous || (previous.publicationMs != null && next.publicationMs != null
    && next.publicationMs < previous.publicationMs)) {
    return { ...next, sourceWatermarkMs: watermark, ageMs: null, discontinuous: true };
  }
  if (previous.sourceWatermarkMs != null && next.sourceMs != null && next.sourceMs <= previous.sourceWatermarkMs) {
    const oldAge = sourceAgeNow(previous);
    return { ...next, sourceWatermarkMs: watermark,
      ageMs: oldAge == null || next.ageMs == null ? null : Math.max(oldAge, next.ageMs) };
  }
  return { ...next, sourceWatermarkMs: watermark };
}

export interface SourceAgeTransfer {
  evidence: Omit<SourceAge, 'received'>;
  sentWallMs: number;
  sentTimelineMs: number;
}

/** Calculate residence in the worker's own clock BEFORE crossing the port.
 * Origin-adjusted timeline is only used to age port delivery, never producer
 * timestamps. Negative/incompatible timelines remain unknown. */
export function transferSourceAge(e: SourceAge): SourceAgeTransfer {
  const { received: _received, ...fields } = e;
  return { evidence: { ...fields, ageMs: sourceAgeNow(e) },
    sentWallMs: Date.now(), sentTimelineMs: performance.timeOrigin + performance.now() };
}

export function receiveSourceAge(t: SourceAgeTransfer): SourceAge {
  const received = localClockStamp();
  const timelineDelta = performance.timeOrigin + received.monotonicMs - t.sentTimelineMs;
  const wallDelta = received.wallMs - t.sentWallMs;
  // timeOrigin/now cross-realm conversion may disagree after OS sleep. A
  // negative timeline cannot prove youth. Positive disagreement conservatively
  // ages evidence, as elapsedSince already does within one realm.
  const portAge = Number.isFinite(timelineDelta) && timelineDelta >= -1 && Number.isFinite(wallDelta)
    ? Math.max(0, timelineDelta, wallDelta) : null;
  return { ...t.evidence, received,
    ageMs: age(t.evidence.ageMs) && portAge != null ? t.evidence.ageMs + portAge : null };
}
